import { useMemo, useState } from "react";
import Lucide from "src/base-components/Lucide";
import ThemeCard from "./ThemeCard";
import CustomThemeBuilder from "./CustomThemeBuilder";
import {
  getAllThemePresets,
  getWebsiteThemePreset,
} from "src/helpers/theme";
import {
  themeCategories,
  ThemeCategoryId,
  WebsiteThemePreset,
  buildFourStopGradient,
} from "src/themes/presets";

interface ThemeLibraryDrawerProps {
  open: boolean;
  activeThemeId: string;
  onClose: () => void;
  onSelectTheme: (themeId: string) => void;
}

const ThemeLibraryDrawer = ({
  open,
  activeThemeId,
  onClose,
  onSelectTheme,
}: ThemeLibraryDrawerProps) => {
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const allPresets = useMemo(
    () => getAllThemePresets(),
    [open, refreshKey, activeThemeId],
  );

  const activePreset = getWebsiteThemePreset(activeThemeId);

  const customPresets = useMemo(
    () => allPresets.filter((preset) => preset.isCustom),
    [allPresets],
  );

  const presetsByCategory = useMemo(() => {
    const grouped = new Map<ThemeCategoryId, WebsiteThemePreset[]>();
    themeCategories.forEach((category) => grouped.set(category.id, []));

    allPresets
      .filter((preset) => !preset.isCustom)
      .forEach((preset) => {
        const list = grouped.get(preset.category) || [];
        list.push(preset);
        grouped.set(preset.category, list);
      });

    return grouped;
  }, [allPresets]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label="Close theme library"
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px] transition-opacity"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Theme library"
        className="theme-library-modal relative z-[9999] flex h-[min(88vh,820px)] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0f1117] shadow-2xl"
      >
        {showCustomBuilder ? (
          <CustomThemeBuilder
            onCancel={() => setShowCustomBuilder(false)}
            onSaved={(themeId) => {
              setShowCustomBuilder(false);
              setRefreshKey((value) => value + 1);
              onSelectTheme(themeId);
            }}
          />
        ) : (
          <>
            <div className="border-b border-white/10 px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-semibold text-white">Theme library</p>
                  <p className="mt-1 text-xs text-slate-400">
                    Choose a dual-color theme visually
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-2 text-slate-400 transition hover:bg-white/5 hover:text-white"
                  aria-label="Close theme library"
                >
                  <Lucide icon="X" className="h-5 w-5" />
                </button>
              </div>

              <div
                className="mt-3 flex items-center gap-3 rounded-xl border border-white/10 px-3 py-2"
                style={{
                  background: buildFourStopGradient(activePreset.colors, "135deg"),
                }}
              >
                <span className="rounded-lg bg-black/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white/90">
                  Active
                </span>
                <span className="truncate text-sm font-medium text-white">
                  {activePreset.name}
                </span>
              </div>
            </div>

            <div className="border-b border-white/10 bg-[#0f1117] px-5 py-3">
              <button
                type="button"
                onClick={() => setShowCustomBuilder(true)}
                className="group flex w-full items-center justify-between rounded-xl border border-dashed border-sky-400/40 bg-sky-500/10 px-4 py-3 text-left transition hover:border-sky-400/70 hover:bg-sky-500/15"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/20 text-sky-300">
                    <Lucide icon="Plus" className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-white">
                      Create custom theme
                    </p>
                    <p className="text-[11px] text-slate-400">
                      Always available — pick your own colors
                    </p>
                  </div>
                </div>
                <Lucide
                  icon="ChevronRight"
                  className="h-4 w-4 text-sky-300 transition group-hover:translate-x-0.5"
                />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-6">
                {customPresets.length > 0 ? (
                  <section className="space-y-3">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Your custom themes
                    </h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {customPresets.map((preset) => (
                        <ThemeCard
                          key={preset.id}
                          preset={preset}
                          isActive={preset.id === activeThemeId}
                          onSelect={onSelectTheme}
                        />
                      ))}
                    </div>
                  </section>
                ) : null}

                {themeCategories.map((category) => {
                  const presets = presetsByCategory.get(category.id) || [];
                  if (presets.length === 0) {
                    return null;
                  }

                  return (
                    <section key={category.id} className="space-y-3">
                      <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        {category.label}
                      </h2>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {presets.map((preset) => (
                          <ThemeCard
                            key={preset.id}
                            preset={preset}
                            isActive={preset.id === activeThemeId}
                            onSelect={onSelectTheme}
                          />
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ThemeLibraryDrawer;
